import { api } from '@/api/client'

/**
 * Nó da árvore organizacional (ROOT → OU → ACCOUNT).
 *
 * `costs_usd` é alinhado por índice com `OrgTreeResponse.months`
 * — `null` indica ausência de dado (não 0).
 */
export interface OrgTreeNode {
  id: string
  type: 'ROOT' | 'OU' | 'ACCOUNT'
  name: string
  costs_usd: (number | null)[]
  children: OrgTreeNode[]
}

export interface OrgTreeResponse {
  snapshot_ts: string
  source: 'snapshot' | 'athena'
  months: string[]
  current_month_is_mtd: boolean
  current_month_as_of: string
  tree: OrgTreeNode
}

/**
 * Busca a árvore organizacional com custos por mês.
 *
 * Backend: GET /api/v1/finops/reports/org-tree?ym=YYYY-MM&refresh=...
 *
 * `refresh='athena'` força recalcular do Athena (lento — até ~90s);
 * default `snapshot` lê da tabela materializada.
 */
export async function fetchOrgTree(p: { ym: string; refresh?: 'snapshot' | 'athena' }) {
  const refresh = p.refresh ?? 'snapshot'
  const url = `/v1/finops/reports/org-tree?ym=${encodeURIComponent(p.ym)}&refresh=${refresh}`
  const r = await api.get<OrgTreeResponse>(url, { timeout: 95000 })
  return r.data
}

/**
 * Exporta o relatório como PDF (template Inovare).
 *
 * Backend: POST /api/v1/finops/reports/org-tree/export-pdf
 * Retorna URL pré-assinada do S3 com expiração.
 */
export async function exportOrgTreePDF(p: { ym: string; source: 'snapshot' | 'athena' }) {
  const r = await api.post<{ url: string; expires_in: number }>(
    '/v1/finops/reports/org-tree/export-pdf',
    p,
  )
  return r.data
}

/**
 * Envia o relatório PDF por email (SES) com anexo.
 *
 * Backend: POST /api/v1/finops/reports/org-tree/email
 * Valida client-side: 1..20 recipients.
 */
export async function emailOrgTree(p: {
  ym: string
  source: 'snapshot' | 'athena'
  recipients: string[]
  subject?: string
}) {
  if (p.recipients.length < 1) throw new Error('at least 1 recipient required')
  if (p.recipients.length > 20) throw new Error('max 20 recipients')
  const r = await api.post('/v1/finops/reports/org-tree/email', p)
  return r.data
}
